import { type InboxTicketRow, OPEN_STATUSES } from "./support-views";

export type ContactGroup = "all" | "tenant" | "owner" | "external";
export type WorkspaceFilter =
  | "all"
  | "mine"
  | "activity"
  | "unassigned"
  | "waiting"
  | "urgent"
  | "overdue"
  | "snoozed"
  | "followup"
  | `person:${string}`;
export type WorkspaceTicket = InboxTicketRow & {
  last_message_at?: string;
  snoozed_until?: string | null;
  follow_up_due?: boolean;
  contactGroup?: Exclude<ContactGroup, "all">;
  searchSnippet?: string;
  searchCommentId?: string | null;
};

export type CompletedConversationBatch = {
  id: string;
  subject: string;
  sentAt: string;
  firstRecipient: string;
  recipientCount: number;
  ticketIds: string[];
};

type BatchMetadata = {
  manual_outbound_batch_id?: unknown;
  manual_outbound_batch_recipient_label?: unknown;
  manual_outbound_batch_recipient_count?: unknown;
  manual_outbound_batch_position?: unknown;
};

export function completedConversationBatches(
  tickets: WorkspaceTicket[]
): CompletedConversationBatch[] {
  const grouped = new Map<string, WorkspaceTicket[]>();
  for (const ticket of tickets) {
    const metadata =
      ticket.metadata && typeof ticket.metadata === "object"
        ? (ticket.metadata as BatchMetadata)
        : null;
    const batchId = metadata?.manual_outbound_batch_id;
    if (typeof batchId !== "string" || !batchId) continue;
    const current = grouped.get(batchId) ?? [];
    current.push(ticket);
    grouped.set(batchId, current);
  }

  const completed: CompletedConversationBatch[] = [];
  for (const [id, batchTickets] of grouped) {
    const firstMetadata = batchTickets[0].metadata as BatchMetadata;
    const expected = Number(
      firstMetadata.manual_outbound_batch_recipient_count
    );
    if (
      !Number.isInteger(expected) ||
      expected < 2 ||
      batchTickets.length !== expected ||
      batchTickets.some((ticket) => !isCompleted(ticket))
    ) {
      continue;
    }
    const ordered = [...batchTickets].sort((a, b) => {
      const aPosition = Number(
        (a.metadata as BatchMetadata).manual_outbound_batch_position
      );
      const bPosition = Number(
        (b.metadata as BatchMetadata).manual_outbound_batch_position
      );
      return aPosition - bPosition;
    });
    const first = ordered[0];
    const firstLabel = (first.metadata as BatchMetadata)
      .manual_outbound_batch_recipient_label;
    const sentAt = ordered
      .map(
        (ticket) =>
          ticket.latestMessageAt ??
          ticket.last_message_at ??
          ticket.created_at ??
          ""
      )
      .sort((a, b) => Date.parse(b) - Date.parse(a))[0];
    completed.push({
      id,
      subject: first.title,
      sentAt,
      firstRecipient:
        typeof firstLabel === "string" && firstLabel
          ? firstLabel
          : first.tenantName,
      recipientCount: expected,
      ticketIds: ordered.map((ticket) => ticket.id),
    });
  }
  return completed.sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt));
}

export function isCompleted(ticket: InboxTicketRow): boolean {
  return !!ticket.archived_at || !OPEN_STATUSES.includes(ticket.status);
}

export function matchesWorkspace(
  ticket: WorkspaceTicket,
  filter: WorkspaceFilter,
  userId: string | null,
  now: number
): boolean {
  if (filter === "activity") return false;
  if (filter === "snoozed") return isSnoozed(ticket, now);
  if (isSnoozed(ticket, now)) return false;
  if (filter === "followup")
    return (
      !!ticket.follow_up_due ||
      (!!ticket.snoozed_until && Date.parse(ticket.snoozed_until) <= now)
    );
  if (filter.startsWith("person:"))
    return ticket.assigned_to === filter.slice(7);
  if (filter === "mine") return !!userId && ticket.assigned_to === userId;
  if (filter === "unassigned") return !ticket.assigned_to;
  if (filter === "waiting") return ticket.status === "waiting";
  if (filter === "urgent") return ticket.priority === "urgent";
  if (filter === "overdue")
    return (
      !isCompleted(ticket) &&
      ticket.status !== "waiting" &&
      now - Date.parse(ticket.last_message_at ?? ticket.created_at ?? "") >=
        86_400_000
    );
  return true;
}

export function contactGroupFor(
  roles: string[],
  hasBooking: boolean
): Exclude<ContactGroup, "all"> {
  if (
    hasBooking ||
    roles.includes("tenant") ||
    (roles.includes("user") && !roles.includes("owner"))
  )
    return "tenant";
  if (roles.includes("owner")) return "owner";
  return "external";
}

export function isSnoozed(ticket: WorkspaceTicket, now = Date.now()): boolean {
  return (
    !isCompleted(ticket) &&
    !!ticket.snoozed_until &&
    Date.parse(ticket.snoozed_until) > now
  );
}

export const CONTACT_GROUPS: { key: ContactGroup; en: string; de: string }[] = [
  { key: "all", en: "All", de: "Alle" },
  { key: "tenant", en: "Tenants", de: "Mieter" },
  { key: "owner", en: "Owners", de: "Eigentümer" },
  { key: "external", en: "External", de: "Extern" },
];
